import { Extension } from '@tiptap/core'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    find: {
      setFindQuery: (query: string) => ReturnType
      findNext: () => ReturnType
      findPrev: () => ReturnType
      clearFind: () => ReturnType
    }
  }
}

export interface FindState {
  query: string
  results: { from: number; to: number }[]
  current: number
  /** Bumped only by query/next/prev - find bar scrolls on this, not on doc edits. */
  scrollGeneration: number
}

export const findPluginKey = new PluginKey<FindState>('find')

export function findAll(doc: ProseMirrorNode, query: string): { from: number; to: number }[] {
  const results: { from: number; to: number }[] = []
  const lower = query.toLowerCase()
  doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return
    const text = node.text.toLowerCase()
    let idx = text.indexOf(lower)
    while (idx !== -1) {
      results.push({ from: pos + idx, to: pos + idx + query.length })
      idx = text.indexOf(lower, idx + 1)
    }
  })
  return results
}

/** Keep the active match near its pre-edit position instead of jumping to match 0. */
export function retainCurrentIndex(
  prevResults: { from: number; to: number }[],
  prevCurrent: number,
  nextResults: { from: number; to: number }[],
  mapPos: (pos: number) => number,
): number {
  if (nextResults.length === 0) return 0
  const prevMatch = prevResults[prevCurrent]
  if (!prevMatch) return 0
  const mapped = mapPos(prevMatch.from)
  let best = 0
  let bestDist = Infinity
  for (let i = 0; i < nextResults.length; i++) {
    const dist = Math.abs(nextResults[i].from - mapped)
    if (dist < bestDist) {
      bestDist = dist
      best = i
    }
  }
  return best
}

export const FindExtension = Extension.create({
  name: 'find',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: findPluginKey,
        state: {
          init: (): FindState => ({ query: '', results: [], current: 0, scrollGeneration: 0 }),
          apply(tr, prev) {
            const meta = tr.getMeta(findPluginKey) as Partial<FindState> | undefined
            if (meta) return { ...prev, ...meta }
            if (tr.docChanged && prev.query) {
              const results = findAll(tr.doc, prev.query)
              const current = retainCurrentIndex(
                prev.results,
                prev.current,
                results,
                (pos) => tr.mapping.map(pos),
              )
              return { ...prev, results, current }
            }
            return prev
          },
        },
        props: {
          decorations(state) {
            const s = findPluginKey.getState(state)
            if (!s || !s.query || s.results.length === 0) return DecorationSet.empty
            const decos = s.results.map((r, i) =>
              Decoration.inline(r.from, r.to, {
                class: i === s.current ? 'find-match find-match--active' : 'find-match',
              })
            )
            return DecorationSet.create(state.doc, decos)
          },
        },
      }),
    ]
  },

  addKeyboardShortcuts() {
    return {
      // Cmd+G / Ctrl+G � next match (only when a find query is active)
      'Mod-g': () => {
        const s = findPluginKey.getState(this.editor.state)
        if (!s?.query) return false
        this.editor.commands.findNext()
        return true
      },
      // Cmd+Shift+G / Ctrl+Shift+G � previous match
      'Mod-Shift-g': () => {
        const s = findPluginKey.getState(this.editor.state)
        if (!s?.query) return false
        this.editor.commands.findPrev()
        return true
      },
    }
  },

  addCommands() {
    return {
      setFindQuery: (query: string) => ({ tr, dispatch, state }) => {
        const s = findPluginKey.getState(state)
        const results = query.trim() ? findAll(state.doc, query) : []
        if (dispatch) {
          dispatch(
            tr.setMeta(findPluginKey, {
              query,
              results,
              current: 0,
              scrollGeneration: (s?.scrollGeneration ?? 0) + 1,
            }),
          )
        }
        return true
      },

      findNext: () => ({ tr, dispatch, state }) => {
        const s = findPluginKey.getState(state)
        if (!s || !s.results.length) return false
        const next = (s.current + 1) % s.results.length
        if (dispatch) {
          dispatch(
            tr.setMeta(findPluginKey, {
              ...s,
              current: next,
              scrollGeneration: s.scrollGeneration + 1,
            }),
          )
        }
        return true
      },

      findPrev: () => ({ tr, dispatch, state }) => {
        const s = findPluginKey.getState(state)
        if (!s || !s.results.length) return false
        const prev = (s.current - 1 + s.results.length) % s.results.length
        if (dispatch) {
          dispatch(
            tr.setMeta(findPluginKey, {
              ...s,
              current: prev,
              scrollGeneration: s.scrollGeneration + 1,
            }),
          )
        }
        return true
      },

      clearFind: () => ({ tr, dispatch }) => {
        if (dispatch) {
          dispatch(
            tr.setMeta(findPluginKey, {
              query: '',
              results: [],
              current: 0,
              scrollGeneration: 0,
            }),
          )
        }
        return true
      },
    }
  },
})
